import {AttrsSchema, Component} from 'panel';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import findLastIndex from 'lodash/findLastIndex';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import ResizeObserver from 'resize-observer-polyfill';

import {truncateMiddle} from 'common/util/string';
import {getModifierKeys} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ScrollBoxMountEvent} from 'common/widgets/scroll-control/types';
import {
  convertOldSortingConfig,
  createRowCompareFunc,
  fillMissingColSortAttrs,
  getNumSegments,
  getSortSelectValueItems,
  invertSortOrder,
  rowSortComparator,
} from 'common/components/data-table/utils';
import {SelectAttrs} from 'common/components/select/types';
import {SortBy, SortOrder} from 'common/charts/visualization/bar-viz/types';

import {template} from './template';
import css from './index.styl?inline';
import {
  CellClickEvent,
  CellRenderContext,
  CollapseChangeEvent,
  ColSortOptionValue,
  ColumnGroup,
  ColumnResizeEvent,
  DataTableAttrs,
  SEGMENT_PREFIX,
  SelectionChangeEvent,
  SortByValueConfig,
  SortChangeEvent,
  TableCell,
  TableColumn,
  TableRow,
  TableSortConfig,
  TableState,
  Theme,
} from './types';
import {Themes} from './themes';

const COMPONENT_NAME = `mp-data-table`;
const TRACK_EVENT = {
  LOADED: `Load`,
  CELL_CLICK: `Cell Click`,
  COLLAPSE_CHANGE: `Collapse Change`,
  SELECTION_CHANGE: `Selection Change`,
  SORT_CHANGE: `Sort Change`,
  PROGRESSIVE_SCROLL: `Progressive Scroll`,
};
const INCREMENTAL_ROWS = 25;
const INITIAL_NUM_DISPLAY_ROWS = INCREMENTAL_ROWS * 4;
const COLUMN_TITLE_MAX_LENGTH = 100;

export const FUNNELS_CHECKBOX_CELL_WIDTH = 40;

/**
 * mp-data-table
 *
 * display mostly numeric data of a repetitive format densely in a scrolling container
 * with a sticky header and sticky columns.
 *
 * examples: retention data, funnels step data
 */
export class DataTable extends Component<TableState, DataTableAttrs> {
  private onResizePointerMove: (ev: PointerEvent) => void;
  private onResizePointerUp: (ev: PointerEvent) => void;
  private onWindowBlur: () => void;
  private tableDiv: HTMLElement;

  static get attrsSchema(): AttrsSchema<DataTableAttrs> {
    return {
      ...super.attrsSchema,
      loading: {
        type: `boolean`,
        description: `Setting this to true (when loading starts) and then false (when loaded) will result in the loading time to be tracked`,
      },
      'multi-select': {
        type: `boolean`,
        description: `Show checkboxes besides each row and allow select all / none of current filtered results`,
      },
      'selection-limit': {
        type: `number`,
        description: `Limit the number of selections allowed. Also disables shift-click.`,
      },
      collapsible: {
        type: `boolean`,
        description: `Allow grouped rows to be collapsible`,
      },
      'default-sort-field': {
        type: `string`,
        description: `Field by which table should be initially sorted. Updating this attribute will reset the sort to default-sort-field.`,
      },
      'default-sort-order': {
        type: `number`,
        default: 1,
        description: `Initial sort order, 1 for ascending, -1 for descending`,
      },
      'default-sorting': {
        type: `json`,
        description: `Initial sort config (replaces 'default-sort-field' and 'default-sort-order' to support multi-sort)`,
        tsType: `TableSortConfig`,
      },
      'has-comparison': {
        type: `boolean`,
        description: `If comparison rows are interleaved in the rows passed to this component's rows prop`,
      },
      'max-body-height': {
        type: `string`,
        default: `100%`,
        description: `Max body height with units, more rows are loaded as user scrolls progressively`,
      },
      'num-sticky-rows': {
        type: `number`,
        default: 0,
        description: `Sticky rows that will not be sorted, that are to be placed at the start of the table, besides title row. These rows are to be indexed by [0, 1, ..., num-sticky-rows) in the table rows passed in by the user. By default, all rows are sortable.`,
      },
      'sticky-columns-count': {
        type: `number`,
        default: 2,
      },
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-table] <event>' bubbled events that can be listened for tracking with name as a property`,
      },
      theme: {
        type: `string`,
        enum: Object.values(Theme),
        default: Theme.Funnels,
      },
      'segmentation-data-table': {
        type: `boolean`,
        description: `Use the new resizable columns feature and related change to page scroll for vertical scroll`,
      },
      'column-width-overrides': {
        type: `json`,
        tsType: `ColumnWidthOverrides`,
      },
    };
  }

  private displayRowsCache: void | TableRow[];
  private cachedRows: TableRow[];
  private loadingStartTimeMs: number;

  constructor() {
    super();
    this.loadingStartTimeMs = Date.now(); // unix milliseconds when loading started
    this.displayRowsCache = null; // cache of filtered + sorted rows, so scrolling is super smooth
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        rows: [],
        columns: [],
        hasBodyScrolled: false,
        numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS,
        sortField: null,
        sortOrder: 1,
        sorting: {sortBy: SortBy.Value as const, sortOrder: SortOrder.Descending, valueField: ``, colSortAttrs: []},
        cellToolTip: null,
        columnWidthOverrides: {},
        tableWidth: null,
        resizeColumnIndex: null,
        hoveredContext: null,
      },
      helpers: {
        COLUMN_TITLE_MAX_LENGTH,
        truncateMiddle,

        getRowsToDisplay: (): TableRow[] => this.getDisplayedRows().slice(0, this.state.numRowsToDisplay),

        getCellLabel: (row: TableRow<TableCell>, col: TableColumn): string => {
          const label = col.labelTransform ? col.labelTransform(row, col.field) : row[col.field];
          if (label === undefined || label === null) {
            return ``;
          }
          return label.toString();
        },

        getCellRenderContexts: (
          cols: TableColumn[],
          row: TableRow,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext[] => {
          return cols.map((col, colIdx) => this.helpers.getCellRenderContext(row, col, colIdx, rowIdx, totalRows));
        },

        getCellRenderContext: (
          row: TableRow,
          col: TableColumn,
          columnIdx: number,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext => ({
          field: col.field,
          label: this.helpers.getCellLabel(row, col),
          isComparison: this.attr(`has-comparison`) && rowIdx % 2 === 1,
          totalRows,
          columnIdx,
          rowIdx,
        }),

        getColumnsToDisplay: () => {
          return this.state.columns.map((col, i) => {
            return {
              ...col,
              width: this.state.columnWidthOverrides[i] || col.width,
            };
          });
        },

        toggleSelected: (ev, row) => {
          ev.stopPropagation(); // so we don't also fire the rowClick event
          document.getSelection().empty(); // so text isn't highlighted on shift click or double click

          if (!row.isSelectDisabled) {
            const isSelected = !row.isSelected;
            const changed = [{isSelected, row}];

            this.dispatchEvent(
              new CustomEvent(`selectionChange`, {
                detail: {
                  changed,
                  isToggleAll: false,
                  rows: this.getDisplayedRows(),
                },
              }) as SelectionChangeEvent,
            );
            this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {isSelected, numChanged: changed.length});
          }
        },

        collapsibleClicked: (ev, row: TableRow) => {
          ev.stopPropagation();
          const isCollapsed = !row.isCollapsed;
          const changed = [{isCollapsed, row}];
          this.dispatchEvent(
            new CustomEvent(`collapseChange`, {
              detail: {
                changed,
                rows: this.getDisplayedRows(),
              },
            }) as CollapseChangeEvent,
          );
          this.helpers.track(TRACK_EVENT.COLLAPSE_CHANGE, {isCollapsed, numChanged: changed.length});
        },

        getSelectionStats: () => {
          const selectableRows = this._getSelectableDisplayedRows();
          const selectedRows = selectableRows.filter((row) => row.isSelected);
          const numSelectable = selectableRows.length;
          const numSelected = selectedRows.length;
          const limit = this.attr(`selection-limit`) || Number.POSITIVE_INFINITY;
          const rowLimit = this.attr(`has-comparison`) ? limit * 2 : limit;
          return {
            numSelectable,
            numSelected,
            allSelected: numSelectable > 0 && numSelected === numSelectable,
            atSelectionLimit: numSelected >= rowLimit,
          };
        },

        getCheckboxState: (item: TableRow<TableCell>) => {
          if (item.isSelected) {
            const options: any = {
              attrs: {
                checked: true,
                disabled: false,
              },
            };
            if (item.checkedColor) {
              options.attrs[`checked-color`] = item.checkedColor;
            }
            return options;
          }

          const {atSelectionLimit} = this.helpers.getSelectionStats();
          if (atSelectionLimit) {
            return {
              selectDisabledReason:
                item.selectDisabledReason || `You can't select more than ${this.attr(`selection-limit`)} items.`,
              attrs: {disabled: true, checked: false},
            };
          }

          return {
            selectDisabledReason: item.selectDisabledReason,
            attrs: {disabled: item.isSelectDisabled, checked: false},
          };
        },

        getCellItem: (row: TableRow<TableCell>, columnIdx: number) => {
          let cell;
          if (row.cells) {
            let cellColumnIdx = columnIdx - this.attr(`sticky-columns-count`);
            cellColumnIdx = this.attr(`multi-select`) ? cellColumnIdx - 1 : cellColumnIdx;
            cell = cellColumnIdx < row.cells.length && cellColumnIdx >= 0 ? row.cells[cellColumnIdx] : null;
          }
          return cell;
        },

        toggleAllSelected: () => {
          // partial -> none, none -> all, all -> none (like gmail)
          const isSelected = this.helpers.getSelectionStats().numSelected === 0;
          const changed = this._getSelectableDisplayedRows()
            .filter((row) => row.isSelected !== isSelected)
            .map((row) => ({isSelected, row}));
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                changed,
                isToggleAll: true,
                rows: this.getDisplayedRows(),
              },
            }) as SelectionChangeEvent,
          );
          this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {isSelected, numChanged: changed.length, toggleAll: true});
        },

        hasClickInteraction(column: TableColumn) {
          return column.isSortable;
        },

        getNumSegments: () => {
          return getNumSegments(this.state.columns);
        },

        handleHeaderTitleClick: (column: TableColumn) => {
          const state = this.state;

          if (column.isSortable) {
            if (this.attr(`segmentation-data-table`)) {
              const {sorting} = this.state;
              let newSortOrder: SortOrder;
              if (sorting.sortBy === SortBy.Value && sorting.valueField === column.field) {
                newSortOrder = invertSortOrder(sorting.sortOrder);
              } else {
                // sort column (valueField) changed
                newSortOrder = SortOrder.Descending;
              }
              const newSorting: SortByValueConfig = {
                sortBy: SortBy.Value,
                sortOrder: newSortOrder,
                valueField: column.field,
                colSortAttrs: sorting.colSortAttrs,
              };
              this.update({sorting: newSorting});
              this.resetOrderedRows();
            } else {
              const sortField = column.field;
              // if same column is clicked, toggle sort order, otherwise keep it same
              const sortOrder = (state.sortField === sortField ? -1 : 1) * state.sortOrder;
              const sortDetail = {sortField, sortOrder};
              this.update(sortDetail);
              this.resetOrderedRows();

              this._emitSortChange(true);
              this.helpers.track(TRACK_EVENT.SORT_CHANGE, {field: sortField, order: sortOrder});
            }
          }
        },

        handleSortSelect: (ev) => {
          const {segmentIndex, sort} = ev.detail.item.value as ColSortOptionValue;
          let {sorting} = this.state;
          sorting.colSortAttrs[segmentIndex] = sort;
          sorting = fillMissingColSortAttrs({columns: this.state.columns, sorting, defaultColSortConfig: sort});
          this.update({sorting: {sortBy: SortBy.Column, colSortAttrs: sorting.colSortAttrs}});
          this.resetOrderedRows();
        },

        getSelectBoundaryEl: () => {
          return this.tableDiv;
        },

        getSortSelectAttrs: (column: TableColumn): SelectAttrs => {
          const labelSortAsc = {sortBy: SortBy.Label, sortOrder: SortOrder.Ascending};
          const labelSortDesc = {sortBy: SortBy.Label, sortOrder: SortOrder.Descending};
          const segmentIndex = parseInt(column.field.slice(SEGMENT_PREFIX.length));
          const colSortConfig = this.state.sorting.colSortAttrs[segmentIndex];
          const sortingByColumns = this.state.sorting.sortBy === SortBy.Column;

          const valueItems = getSortSelectValueItems({
            colSortConfig,
            segmentIndex,
            sortingByColumns,
            columns: this.state.columns,
          });

          return {
            'menu-width': 190,
            sections: [
              {
                items: [
                  {
                    icon: `sort-alpha-asc`,
                    label: `Alpha A → Z`,
                    value: {
                      segmentIndex,
                      sort: labelSortAsc,
                    } as ColSortOptionValue,
                    isSelected: sortingByColumns && isEqual(labelSortAsc, colSortConfig),
                  },
                  {
                    icon: `sort-alpha-desc`,
                    label: `Alpha Z → A`,
                    value: {
                      segmentIndex,
                      sort: labelSortDesc,
                    } as ColSortOptionValue,
                    isSelected: sortingByColumns && isEqual(labelSortDesc, colSortConfig),
                  },
                  ...valueItems,
                ],
              },
            ],
          };
        },

        handleCellClick: (ev, row, field, context) => {
          this.dispatchEvent(
            new CustomEvent(`cellClick`, {
              detail: {
                row,
                field,
                context,
                target: ev.target,
                modifierKeys: getModifierKeys(ev),
              },
            }) as CellClickEvent,
          );
          this.helpers.track(TRACK_EVENT.CELL_CLICK, {field});
        },

        handleCellPointerEnter: (context) => {
          this.update({hoveredContext: context});
        },

        handleCellPointerLeave: () => {
          this.update({hoveredContext: null});
        },

        handleScroll: debounce(
          () => {
            // dispatch a single scroll event to invalidate any positioning of popovers
            this.dispatchEvent(new CustomEvent(`tableScroll`));

            const focused = this.el.querySelectorAll(`:focus`) || [];
            focused.forEach((f) => f.blur());
          },
          300,
          {leading: true},
        ),

        handleHeaderMouseEnter: (ev, column: TableColumn) => {
          const {x: cellX} = ev.target.getBoundingClientRect();
          const {x: wrapperX} = this.getBoundingClientRect();

          if (column.tooltip) {
            this.update({
              cellToolTip: {
                text: column.tooltip,
                left: cellX - wrapperX,
                // use the parent height because we know the event is fired
                // on a wrapped svg-icon within a sticky header cell
                top: ev.target.parentNode.clientHeight,
              },
            });
          }
        },

        handleHeaderMouseLeave: () => {
          this.update({cellToolTip: null});
        },

        setIntersectionEl: (el: HTMLElement) => {
          const observer = new IntersectionObserver(this.incrementDisplayedRows.bind(this), {
            root: this.el.querySelector(`.mp-table`),
          });

          observer.observe(el);

          this.onDisconnected(function () {
            observer.disconnect();
          });
        },

        track: (event, props) => {
          if (this.attr(`track-name`)) {
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: `[${COMPONENT_NAME}] ${event}`,
                  props: {
                    name: this.attr(`track-name`),
                    ...props,
                  },
                },
              }),
            );
          }
        },

        trackLoaded: () => {
          this.helpers.track(TRACK_EVENT.LOADED, {
            durationMs: Date.now() - this.loadingStartTimeMs,
            numRows: this.state.rows && this.state.rows.length,
          });
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    const columnWidthOverrides = this.attr(`column-width-overrides`);
    if (!isEmpty(columnWidthOverrides)) {
      this.update({columnWidthOverrides});
    }

    this.dispatchEvent(
      new CustomEvent(`scrollBoxMount`, {
        detail: {scrollBox: this.getScrollBox()},
        bubbles: true,
        composed: true,
      }) as ScrollBoxMountEvent,
    );

    requestAnimationFrame(() => {
      if (!this.attr(`loading`)) {
        this.helpers.trackLoaded();
      }

      if (this.attr(`default-sort-field`) || this.attr(`default-sort-order`)) {
        this._emitSortChange(false);
      }
    });
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanResizeEventListeners();
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `default-sort-field`) {
      // override state from attr if specified
      const sortField = this.attr(attr);
      const {sortOrder} = this.state;
      this.update({sortField, sorting: convertOldSortingConfig({sortOrder, sortField})});
      this.resetOrderedRows();
      this._emitSortChange(false);
    } else if (attr === `default-sort-order`) {
      const sortOrder = this.attr(attr);
      const {sortField} = this.state;
      this.update({sortOrder, ...(sortField ? {sorting: convertOldSortingConfig({sortOrder, sortField})} : {})});
      this.resetOrderedRows();
      this._emitSortChange(false);
    } else if (attr === `default-sorting`) {
      const sorting = this.attr(attr);
      this.update({sorting});
      this.maybeUpdateSortingFromColumns({sorting, columns: this.state.columns});
      this.resetOrderedRows();
      this._emitSortChange(false);
    } else if (attr === `loading`) {
      if (this.attr(attr)) {
        this.loadingStartTimeMs = Date.now();
      } else {
        this.helpers.trackLoaded();
      }
    } else if (attr === `column-width-overrides`) {
      const columnWidthOverrides = this.attr(`column-width-overrides`);
      if (isEmpty(this.state.columnWidthOverrides) && !isEmpty(columnWidthOverrides)) {
        this.update({columnWidthOverrides});
      }
    }
  }

  /** Clears displayRowsCache, next getDisplayedRows() will do a sort */
  public resetOrderedRows() {
    this.displayRowsCache = null;
    this.update({numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS});
  }

  /** scroll the variable section of the table to align column $index with the end of the sticky section. */
  public scrollToDataColumn(index: number) {
    const target: HTMLElement = this.el.querySelector(
      `.table-header .data-columns-outer .title-cell:nth-of-type(${index})`,
    );
    const firstNonStickyCol: HTMLElement = this.el.querySelector(
      `.table-header .data-columns-outer .title-cell:first-child`,
    );
    const leftOffset = firstNonStickyCol ? firstNonStickyCol.offsetLeft : 0;
    this.getScrollBox().scroll(target.offsetLeft - leftOffset, 0);
  }

  public getScrollBox() {
    return this.attr(`segmentation-data-table`)
      ? this.el.querySelector(`.scrollable-columns`)
      : this.el.querySelector(`.mp-table`);
  }

  public getTheme() {
    return Themes[this.attr(`theme`)];
  }

  /** @param val - pass via prop for perf reasons */
  set rows(val: Array<TableRow<TableCell>>) {
    const numRowsToDisplay = this.state.numRowsToDisplay || INITIAL_NUM_DISPLAY_ROWS;

    this.update({
      rows: val,
      // clamp numRowsToDisplay between INITIAL_NUM_DISPLAY_ROWS and val.length
      numRowsToDisplay: Math.max(Math.min(val.length, numRowsToDisplay), INITIAL_NUM_DISPLAY_ROWS),
    });
    this.displayRowsCache = null;

    if (!isEqual(val, this.cachedRows)) {
      // If the rows are the same, don't emit a `sortChange` event. Without this check, there could be an infinite
      // render loop:
      // Setting rows emits a `sortChange` event which results in the call-site updating state. The call-site will
      // regenerate rows. Because snabbdom performs an reference equivalency check, this row setter will get called
      // even if the rows haven't changed. Then this row setter would emit `sortChange` again, resulting in a
      // never-ending loop.
      this._emitSortChange(false);
    }
    this.cachedRows = val;
  }

  getSelectColumn(): TableColumn[] {
    if (this.attr(`multi-select`)) {
      return [
        {field: `isSelected`, type: `checkbox`, sticky: true, width: FUNNELS_CHECKBOX_CELL_WIDTH, fixedWidth: true},
      ];
    } else {
      return [];
    }
  }

  getCollapsibleColumn(): TableColumn[] {
    if (this.attr(`collapsible`)) {
      return [{field: `isCollapsible`, type: `collapsible`, sticky: true, width: 22, fixedWidth: true}];
    } else {
      return [];
    }
  }

  set columnGroups(groups: Array<ColumnGroup | TableColumn>) {
    let flatColumns = groups
      .map((group, groupIndex, allGroups) => {
        let columns = [];
        if (groupIndex === 0) {
          columns = columns.concat(this.getSelectColumn(), this.getCollapsibleColumn());
        }
        if (`columns` in group) {
          columns = columns.concat(group.columns);
        } else {
          columns = columns.concat([group]);
        }

        return columns.map((col, colIndex, allCols) => {
          const first = colIndex === 0;
          const last = colIndex === allCols.length - 1;
          const rightmost = last && groupIndex === allGroups.length - 1;
          return {
            ...col,
            // Sticky columns should be resizeable by default
            fixedWidth: col.fixedWidth ?? !col.sticky,
            first,
            last,
            rightmost,
          };
        });
      })
      .flat();

    const lastStickyColumn = findLastIndex(flatColumns, (col) => col.sticky);
    flatColumns = flatColumns.map((col, colIndex) => {
      return {
        ...col,
        lastSticky: colIndex === lastStickyColumn,
      };
    });

    this.maybeUpdateSortingFromColumns({sorting: this.state.sorting, columns: flatColumns});
    this.update({columns: flatColumns});

    this.displayRowsCache = null;
  }

  maybeUpdateSortingFromColumns({sorting, columns}: {sorting: TableSortConfig; columns: TableColumn[]}): void {
    if (columns.length && sorting.sortBy === SortBy.Column) {
      sorting = fillMissingColSortAttrs({columns, sorting});
      this.update({sorting});
    }
  }

  onResizePointerDown({
    ev,
    resizeColumnIndex,
    initialWidth,
    minWidth,
  }: {
    ev: PointerEvent;
    resizeColumnIndex: number;
    initialWidth: number;
    minWidth: number;
  }) {
    ev.preventDefault();
    this.update({
      resizeColumnIndex,
    });

    // cannot use currentTarget to store element reference
    const handleEl = ev.target as HTMLElement;
    const initialLeft = handleEl.offsetLeft;
    const lowestDelta = (initialWidth - minWidth) * -1;
    const initialX = ev.screenX;
    this.onResizePointerMove = (pointerMoveEvent) =>
      this.handleResizePointerMove({
        initialX: ev.screenX,
        initialLeft,
        handleEl,
        lowestDelta,
        ev: pointerMoveEvent,
      });
    this.onResizePointerUp = (pointerUpEvent) =>
      this.handleResizePointerUp({initialX, initialWidth, minWidth, ev: pointerUpEvent});
    this.onWindowBlur = () => this.resetResizeState();

    window.addEventListener(`pointermove`, this.onResizePointerMove, true);
    window.addEventListener(`pointerup`, this.onResizePointerUp, true);
    window.addEventListener(`blur`, this.onWindowBlur, true);
  }

  handleResizePointerMove({initialX, initialLeft, handleEl, lowestDelta, ev}) {
    ev.stopPropagation();
    const delta = ev.screenX - initialX;
    const clampedDelta = delta < lowestDelta ? lowestDelta : delta;
    // outside of panel render for performance
    handleEl.style.left = `${initialLeft + clampedDelta}px`;
  }

  handleResizePointerUp({initialX, initialWidth, minWidth, ev}) {
    const delta = ev.screenX - initialX;
    const {columnWidthOverrides, resizeColumnIndex} = this.state;
    columnWidthOverrides[resizeColumnIndex] = Math.max(initialWidth + delta, minWidth);
    this.update({columnWidthOverrides: this.state.columnWidthOverrides});
    this.resetResizeState();
    this.dispatchEvent(new CustomEvent(`columnResize`, {detail: {columnWidthOverrides}}) as ColumnResizeEvent);
  }

  resetResizeState() {
    this.cleanResizeEventListeners();
    this.update({
      resizeColumnIndex: null,
    });
  }

  cleanResizeEventListeners() {
    window.removeEventListener(`blur`, this.onWindowBlur, true);
    window.removeEventListener(`pointermove`, this.onResizePointerMove, true);
    window.removeEventListener(`pointerup`, this.onResizePointerUp, true);
    this.onWindowBlur = null;
    this.onResizePointerMove = null;
    this.onResizePointerUp = null;
  }

  tableInserted(el: HTMLElement) {
    /** mp-select currently needs a boundaryEl so it always opens down (prevents overflow issue) */
    this.tableDiv = el;

    /** div.scrollableColumns needs an explicit width for horizontal scroll to work */
    const updateWidth = debounce(() => {
      this.update({tableWidth: el.clientWidth});
    }, 500);
    const widthObserver = new ResizeObserver(updateWidth);
    widthObserver.observe(el);

    this.onDisconnected(() => {
      widthObserver.disconnect();
      this.tableDiv = null;
    });
  }

  private _emitSortChange(isHeaderClick: boolean) {
    this.dispatchEvent(
      new CustomEvent(`sortChange`, {
        detail: {
          sortField: this.state.sortField,
          sortOrder: this.state.sortOrder,
          rows: this.getDisplayedRows(),
          isHeaderClick,
        },
      }) as SortChangeEvent,
    );
  }

  private incrementDisplayedRows() {
    const {numRowsToDisplay} = this.state;
    const totalRows = this.getDisplayedRows().length;

    if (numRowsToDisplay >= totalRows) {
      return;
    }

    this.update({
      numRowsToDisplay: Math.min(totalRows, numRowsToDisplay + INCREMENTAL_ROWS),
    });
  }

  private getDisplayedRows(): TableRow[] {
    // filter + sort is a significant overhead for tables with 1000s of rows
    // we memoize the display rows in a temporary cache so progressive scrolling is cheap
    if (this.displayRowsCache) {
      return this.displayRowsCache;
    }

    const {rows, sortField, sortOrder, sorting} = this.state;
    const columns = this.helpers.getColumnsToDisplay();

    const stickyRows = rows.slice(0, this.attr(`num-sticky-rows`));
    const sortableRows = rows.slice(this.attr(`num-sticky-rows`));
    let displaySortableRows = sortableRows;

    const shouldSortOld = sortField && !this.attr(`segmentation-data-table`);
    const shouldSortNew = sorting && this.attr(`segmentation-data-table`);
    if (shouldSortOld || shouldSortNew) {
      let compareFunc: (a: TableRow, b: TableRow) => number;
      if (shouldSortNew) {
        const currentTimePeriodRows = this.attr(`has-comparison`)
          ? sortableRows.filter((_row, i) => i % 2 === 0)
          : sortableRows;
        compareFunc = createRowCompareFunc({sortConfig: sorting, columns, rows: currentTimePeriodRows});
      } else {
        const sortColumn = columns.find((col) => col.field === sortField);
        const sortTransform = sortColumn?.sortTransform;
        compareFunc = (a, b) => rowSortComparator(a, b, {sortOrder, sortTransform, sortField});
      }

      // single sort function is faster than lexicalCompose
      // sorting is usually the n*log(n) bottleneck so we want it to be fast
      if (this.attr(`has-comparison`)) {
        // sort row pairs together by the first row (current time period)
        const sortedPairs = chunk(displaySortableRows, 2).sort((pairA, pairB) => {
          return compareFunc(pairA[0], pairB[0]);
        });
        displaySortableRows = flatten(sortedPairs);
      } else {
        displaySortableRows.sort((a, b) => {
          return compareFunc(a, b);
        });
      }
    }

    const displayRows = stickyRows.concat(displaySortableRows);
    return (this.displayRowsCache = displayRows);
  }

  private _getSelectableDisplayedRows(): Array<TableRow<TableCell>> {
    return this.getDisplayedRows().filter((row) => !row.isSelectDisabled);
  }
}

export const DataTableEl = defineAndMakeJsxFactory(COMPONENT_NAME, DataTable);
